import { cn } from '@plato/ui/util/classes';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { ForwardedRef, InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { forwardRef } from 'react';

// TODO: refactor this to use a common component when all the styles are unified

export const inputStyle = cva(
    ['block w-full border-input px-2 py-2.5', 'text-sm', 'placeholder:text-neutral-600', 'rounded-md border'],
    {
        variants: {
            variant: {
                border: 'border-neutral-200',
                default: 'border-transparent',
                error: 'border-red-500',
                readonly: 'cursor-not-allowed bg-neutral-50 text-neutral-600',
            },

            // interactive=false to style non-input elements
            interactive: {
                true: [
                    'active:ring-blue-200 active:ring-1',
                    'disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-600',
                    'placeholder:text-neutral-600',
                    'enabled:hover:border-neutral-400',
                    'focus:border-blue-500 focus:outline-hidden',
                    'invalid:border-red-500',
                ],
                false: null,
            },

            hover: {
                always: 'hover:border-neutral-400',
                never: 'hover:border-transparent',
            },
        },
        defaultVariants: {
            interactive: true,
            variant: 'default',
        },
    },
);

type InputStyleProps = VariantProps<typeof inputStyle>;

export type CrmInputProps = InputHTMLAttributes<HTMLInputElement> & {
    variant?: InputStyleProps['variant'];
};

/**
 * Just an input element with our designs applied.
 */
export const CrmInput = forwardRef(
    ({ className, variant, ...props }: CrmInputProps, forwardedRef: ForwardedRef<HTMLInputElement>) => {
        return <input ref={forwardedRef} {...props} className={cn(inputStyle({ variant }), className)} />;
    },
);

CrmInput.displayName = 'CrmInput';

export type CrmTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
    variant?: InputStyleProps['variant'];
};

/**
 * The textarea counterpart to `CrmInput`, for fields with `format: 'multiline'`.
 * `field-sizing-content` grows it with its content; `max-h-64` stops a long note
 * from pushing the rest of the tab off screen.
 */
export const CrmTextarea = forwardRef(
    ({ className, variant, ...props }: CrmTextareaProps, forwardedRef: ForwardedRef<HTMLTextAreaElement>) => {
        return (
            <textarea
                ref={forwardedRef}
                rows={3}
                {...props}
                className={cn(
                    inputStyle({ variant }),
                    'field-sizing-content max-h-64 resize-none',
                    className,
                )}
            />
        );
    },
);

CrmTextarea.displayName = 'CrmTextarea';
